import { useEffect, useRef, useState } from "react";
import { CheckCircle, Sparkles } from "lucide-react";

const workflowPoints = [
  "Topic and niche, locked in",
  "Built for your ideal reader",
  "A full chapter structure",
  "One publish-ready prompt",
];

const createRoundRectPath = (context, x, y, width, height, radius) => {
  if (typeof context.roundRect === "function") {
    context.roundRect(x, y, width, height, radius);
    return;
  }

  context.moveTo(x + radius, y);
  context.lineTo(x + width - radius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + radius);
  context.lineTo(x + width, y + height - radius);
  context.quadraticCurveTo(
    x + width,
    y + height,
    x + width - radius,
    y + height
  );
  context.lineTo(x + radius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - radius);
  context.lineTo(x, y + radius);
  context.quadraticCurveTo(x, y, x + radius, y);
};

const createLabelTexture = (THREE, label, accentColor) => {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 220;

  const context = canvas.getContext("2d");
  context.clearRect(0, 0, canvas.width, canvas.height);

  const gradient = context.createLinearGradient(
    0,
    0,
    canvas.width,
    canvas.height
  );
  gradient.addColorStop(0, "rgba(17, 24, 39, 0.92)");
  gradient.addColorStop(1, "rgba(46, 16, 101, 0.86)");

  context.beginPath();
  createRoundRectPath(context, 18, 18, 476, 184, 34);
  context.fillStyle = gradient;
  context.fill();

  context.lineWidth = 5;
  context.strokeStyle = accentColor;
  context.stroke();

  context.font = "700 42px Inter, Arial, sans-serif";
  context.fillStyle = "#ffffff";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(label, 256, 92);

  context.font = "500 24px Inter, Arial, sans-serif";
  context.fillStyle = "rgba(221, 214, 254, 0.88)";
  context.fillText("AI Ebook Planner", 256, 142);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
};

const addPageLines = (THREE, group, sign, material) => {
  const lineGeometry = new THREE.BoxGeometry(0.84, 0.014, 0.018);

  for (let index = 0; index < 6; index += 1) {
    const line = new THREE.Mesh(lineGeometry, material);
    line.position.set(sign * 0.1, 0.048, -0.62 + index * 0.22);
    line.scale.x = index % 3 === 0 ? 0.74 : 1;
    group.add(line);
  }
};

const disposeScene = (scene, renderer, mount, animationFrame) => {
  if (animationFrame.current) {
    cancelAnimationFrame(animationFrame.current);
  }

  scene.traverse((object) => {
    if (object.geometry) {
      object.geometry.dispose();
    }

    if (object.material) {
      const materials = Array.isArray(object.material)
        ? object.material
        : [object.material];

      materials.forEach((material) => {
        if (material.map) {
          material.map.dispose();
        }
        material.dispose();
      });
    }
  });

  renderer.dispose();

  if (renderer.domElement && mount.contains(renderer.domElement)) {
    mount.removeChild(renderer.domElement);
  }
};

const buildScene = (THREE, mount, prefersReducedMotion, onReady) => {
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
  camera.position.set(0, 2.2, 5.55);

  const renderer = new THREE.WebGLRenderer({
    alpha: true,
    antialias: true,
    powerPreference: "high-performance",
    preserveDrawingBuffer: true,
  });

  camera.lookAt(0, 0.34, 0);

  renderer.setClearColor(0x000000, 0);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.domElement.setAttribute("data-three-ebook-canvas", "true");
  renderer.domElement.style.display = "block";
  renderer.domElement.style.width = "100%";
  renderer.domElement.style.height = "100%";
  renderer.domElement.style.touchAction = "pan-y";
  mount.appendChild(renderer.domElement);

  scene.add(new THREE.AmbientLight(0x8894ff, 0.9));

  const keyLight = new THREE.DirectionalLight(0xffffff, 2.2);
  keyLight.position.set(2.4, 4.2, 3.4);
  scene.add(keyLight);

  const rimLight = new THREE.PointLight(0xa855f7, 6, 9);
  rimLight.position.set(-2.6, 1.8, 2.6);
  scene.add(rimLight);

  const blueLight = new THREE.PointLight(0x38bdf8, 4, 8);
  blueLight.position.set(2.8, 1.1, 1.4);
  scene.add(blueLight);

  const stage = new THREE.Group();
  stage.rotation.x = -0.12;
  scene.add(stage);

  const coverMaterial = new THREE.MeshStandardMaterial({
    color: 0x5b21b6,
    emissive: 0x1d0f3f,
    emissiveIntensity: 0.38,
    metalness: 0.22,
    roughness: 0.42,
  });

  const pageMaterial = new THREE.MeshStandardMaterial({
    color: 0xf3efff,
    metalness: 0.02,
    roughness: 0.64,
  });

  const pageLineMaterial = new THREE.MeshBasicMaterial({
    color: 0x8b5cf6,
    transparent: true,
    opacity: 0.36,
  });

  const spineMaterial = new THREE.MeshStandardMaterial({
    color: 0x111827,
    emissive: 0x312e81,
    emissiveIntensity: 0.5,
    metalness: 0.1,
    roughness: 0.5,
  });

  const createBookSide = (sign) => {
    const side = new THREE.Group();
    side.position.x = sign * 0.82;
    side.rotation.z = sign * -0.11;

    const cover = new THREE.Mesh(
      new THREE.BoxGeometry(1.66, 0.08, 2.2),
      coverMaterial
    );
    cover.position.y = -0.08;
    side.add(cover);

    const pages = new THREE.Mesh(
      new THREE.BoxGeometry(1.52, 0.065, 2.02),
      pageMaterial
    );
    pages.position.y = 0.01;
    side.add(pages);

    addPageLines(THREE, side, sign, pageLineMaterial);
    return side;
  };

  stage.add(createBookSide(-1));
  stage.add(createBookSide(1));

  const spine = new THREE.Mesh(
    new THREE.BoxGeometry(0.16, 0.16, 2.28),
    spineMaterial
  );
  spine.position.y = -0.04;
  stage.add(spine);

  const coreMaterial = new THREE.MeshStandardMaterial({
    color: 0x60a5fa,
    emissive: 0x2563eb,
    emissiveIntensity: 1.2,
    metalness: 0.32,
    roughness: 0.28,
  });

  // Everything that orbits lives in a group lifted clear of the book. Because
  // the orbit group is a child of the stage, it shares the book's tilt and
  // pointer rotation, so this offset is fixed in book-space: the rings and
  // cards always arc ABOVE the open pages and never slice through them.
  const ORBIT_PLANE_Y = 0.66;
  const orbit = new THREE.Group();
  orbit.position.y = ORBIT_PLANE_Y;
  stage.add(orbit);

  const core = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.34, 2),
    coreMaterial
  );
  orbit.add(core);

  const ringMaterial = new THREE.MeshBasicMaterial({
    color: 0x7dd3fc,
    transparent: true,
    opacity: 0.4,
    depthWrite: false,
  });

  // Near-horizontal orbital tracks that lie in the raised plane around the
  // core. A gentle tilt keeps a 3D feel without ever dipping toward the book.
  const ringA = new THREE.Mesh(
    new THREE.TorusGeometry(2.04, 0.009, 12, 220),
    ringMaterial
  );
  ringA.rotation.x = Math.PI / 2 + 0.08;
  ringA.rotation.z = 0.12;
  ringA.renderOrder = 1;
  orbit.add(ringA);

  const ringB = new THREE.Mesh(
    new THREE.TorusGeometry(1.46, 0.007, 12, 200),
    ringMaterial.clone()
  );
  ringB.material.opacity = 0.26;
  ringB.rotation.x = Math.PI / 2 - 0.06;
  ringB.rotation.z = -0.18;
  ringB.renderOrder = 1;
  orbit.add(ringB);

  const labels = [
    ["TOPIC", "rgba(96, 165, 250, 0.95)"],
    ["AUDIENCE", "rgba(52, 211, 153, 0.95)"],
    ["CHAPTERS", "rgba(216, 180, 254, 0.95)"],
    ["PROMPT", "rgba(251, 191, 36, 0.95)"],
  ];

  const cardGeometry = new THREE.PlaneGeometry(1.16, 0.5);
  const cards = labels.map(([label, accent], index) => {
    const material = new THREE.MeshBasicMaterial({
      map: createLabelTexture(THREE, label, accent),
      transparent: true,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const card = new THREE.Mesh(cardGeometry, material);
    card.userData.phase = (index / labels.length) * Math.PI * 2;
    // Draw cards after the rings so the labels stay crisp where they overlap,
    // while depthTest (left on) still lets the solid book occlude back-side
    // cards as they swing behind it.
    card.renderOrder = 2;
    orbit.add(card);
    return card;
  });

  const pointer = { x: 0, y: 0 };
  const sceneLayout = {
    cardRadius: 2.0,
    cardDepth: 1.34,
    cardSwing: 0.1,
  };
  const handlePointerMove = (event) => {
    const rect = mount.getBoundingClientRect();
    pointer.x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
    pointer.y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
  };
  // Ease the parallax back to the resting sway when the cursor leaves or a
  // finger lifts, so the scene never sticks in a rotated pose on mobile.
  const handlePointerRelease = () => {
    pointer.x = 0;
    pointer.y = 0;
  };
  mount.addEventListener("pointermove", handlePointerMove);
  mount.addEventListener("pointerleave", handlePointerRelease);
  mount.addEventListener("pointerup", handlePointerRelease);
  mount.addEventListener("pointercancel", handlePointerRelease);

  const resize = () => {
    const width = Math.max(320, mount.clientWidth || 640);
    const height = Math.max(300, mount.clientHeight || 420);
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.75);

    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.position.y = width < 520 ? 2.1 : 2.3;
    camera.position.z = width < 520 ? 6.7 : width < 680 ? 6.05 : 5.55;
    sceneLayout.cardRadius = width < 520 ? 1.62 : width < 680 ? 1.82 : 2.0;
    sceneLayout.cardDepth = width < 520 ? 1.04 : width < 680 ? 1.18 : 1.34;
    cards.forEach((card) => {
      const cardScale = width < 520 ? 0.8 : width < 680 ? 0.9 : 1;
      card.scale.setScalar(cardScale);
    });
    camera.lookAt(0, 0.34, 0);
    camera.updateProjectionMatrix();
  };

  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(mount);
  resize();

  let isVisible = true;
  const visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      isVisible = entry.isIntersecting;
    },
    { threshold: 0.08 }
  );
  visibilityObserver.observe(mount);

  const startTime = window.performance.now();
  const animationFrame = { current: null };
  let frame = 0;
  let readySent = false;

  const render = () => {
    const elapsed = (window.performance.now() - startTime) / 1000;
    const motion = prefersReducedMotion ? 0 : elapsed;

    // A gentle, always-on sway keeps the scene feeling alive on touch devices,
    // where there is no hover to drive the parallax. Finger drag / mouse move
    // (pointer.x/y) adds to it for direct interaction on phones and desktop.
    const autoYaw = Math.sin(motion * 0.3) * 0.16;
    const targetYaw = autoYaw + pointer.x * 0.24;
    const targetPitch =
      -0.12 + pointer.y * -0.06 + Math.sin(motion * 0.24) * 0.015;
    stage.rotation.y += (targetYaw - stage.rotation.y) * 0.045;
    stage.rotation.x += (targetPitch - stage.rotation.x) * 0.045;

    core.rotation.y = motion * 0.85;
    core.rotation.x = 0.3 + motion * 0.35;
    ringA.rotation.z = 0.12 + Math.sin(motion * 0.4) * 0.05;
    ringB.rotation.z = -0.18 + Math.cos(motion * 0.35) * 0.05;

    cards.forEach((card, index) => {
      const phase = card.userData.phase + motion * 0.4;
      card.position.set(
        Math.cos(phase) * sceneLayout.cardRadius,
        Math.sin(phase * 1.3) * sceneLayout.cardSwing,
        Math.sin(phase) * sceneLayout.cardDepth
      );
      card.rotation.z = Math.sin(phase + index) * 0.06;
      card.lookAt(camera.position);
    });

    renderer.render(scene, camera);
    renderer.domElement.dataset.frame = String(frame);
    frame += 1;

    if (!readySent) {
      readySent = true;
      renderer.domElement.dataset.ready = "true";
      onReady();
    }

    if (!prefersReducedMotion) {
      animationFrame.current = requestAnimationFrame(render);
    }
  };

  const scheduleRender = () => {
    if (!isVisible && !prefersReducedMotion) {
      animationFrame.current = requestAnimationFrame(scheduleRender);
      return;
    }
    render();
  };

  scheduleRender();

  return () => {
    mount.removeEventListener("pointermove", handlePointerMove);
    mount.removeEventListener("pointerleave", handlePointerRelease);
    mount.removeEventListener("pointerup", handlePointerRelease);
    mount.removeEventListener("pointercancel", handlePointerRelease);
    resizeObserver.disconnect();
    visibilityObserver.disconnect();
    disposeScene(scene, renderer, mount, animationFrame);
  };
};

const StaticBookPreview = ({ isHidden }) => (
  <div
    aria-hidden="true"
    className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${
      isHidden ? "opacity-0" : "opacity-100"
    }`}
  >
    <div className="relative h-64 w-full max-w-md">
      <div className="absolute left-1/2 top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full border border-sky-300/30" />
      <div className="absolute left-[18%] top-[38%] h-28 w-36 -rotate-6 rounded-xl border border-white/15 bg-gradient-to-br from-purple-500/30 to-blue-500/10 shadow-glow" />
      <div className="absolute right-[18%] top-[38%] h-28 w-36 rotate-6 rounded-xl border border-white/15 bg-gradient-to-br from-sky-400/20 to-fuchsia-500/20 shadow-glow-blue" />
      <div className="absolute left-1/2 top-[32%] h-16 w-16 -translate-x-1/2 rounded-2xl border border-white/25 bg-white/10 shadow-glow" />
    </div>
  </div>
);

const EbookOrbitShowcase = () => {
  const mountRef = useRef(null);
  const cleanupRef = useRef(null);
  const startedRef = useRef(false);
  const [sceneState, setSceneState] = useState("idle");

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    let cancelled = false;

    const startScene = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      setSceneState("loading");

      import("three")
        .then((THREE) => {
          if (cancelled) return;
          const prefersReducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
          ).matches;
          cleanupRef.current = buildScene(
            THREE,
            mount,
            prefersReducedMotion,
            () => {
              if (!cancelled) {
                setSceneState("ready");
              }
            }
          );
        })
        .catch(() => {
          if (!cancelled) {
            setSceneState("fallback");
          }
        });
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startScene();
          observer.disconnect();
        }
      },
      { rootMargin: "220px" }
    );

    observer.observe(mount);

    return () => {
      cancelled = true;
      observer.disconnect();
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  const canvasReady = sceneState === "ready";

  return (
    <section
      className="relative w-screen overflow-hidden py-16 sm:py-20 lg:py-24"
      style={{ marginLeft: "calc(50% - 50vw)", marginTop: "-1px" }}
    >
      {/* Full-bleed, borderless glow band. It carries the hero's color straight
          down into the page and fades to transparent on every edge, so the
          section reads as part of the background. It stretches edge to edge,
          starts flush under the hero, and never looks like a boxed-in panel. */}
      <div
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(75% 120% at 50% 50%, rgba(91, 33, 182, 0.18), rgba(37, 99, 235, 0.07) 50%, transparent 78%)",
          // The glow is taller than the section, so without this it was cut
          // off flat at the top (under the hero) and bottom (above the step
          // tabs). Fading it out at both ends blends the band into the page.
          maskImage:
            "linear-gradient(to bottom, transparent 0%, #000 28%, #000 72%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, #000 28%, #000 72%, transparent 100%)",
        }}
      />

      <div className="mx-auto grid max-w-6xl gap-8 px-5 sm:px-6 md:px-8 lg:px-10 lg:grid-cols-[0.92fr_1.08fr] lg:items-center lg:gap-10">
        <div className="max-w-xl">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold text-sky-100 backdrop-blur-md sm:text-sm">
            <Sparkles size={14} className="text-sky-300" />
            Plan your whole book in minutes
          </div>
          <h2 className="font-display text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl md:text-5xl">
            From one idea to a finished book.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-gray-300 sm:text-lg">
            AI Ebook Planner takes your topic and builds the rest of your plan
            around it: your target audience, a chapter-by-chapter structure, and
            one ready-to-paste prompt that writes the whole draft in the AI tool
            you already use.
          </p>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {workflowPoints.map((point) => (
              <div
                key={point}
                className="flex items-center gap-3 text-sm text-gray-200 sm:text-base"
              >
                <CheckCircle
                  size={18}
                  className="flex-shrink-0 text-emerald-400"
                />
                <span>{point}</span>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => {
              const formSection = document.querySelector(".step-grid");
              if (formSection) {
                formSection.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }
            }}
            className="btn-sheen mt-7 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-bold text-gray-950 shadow-glow transition-all duration-200 hover:bg-sky-50"
          >
            <Sparkles size={16} />
            Start with your topic
          </button>
        </div>

        <div className="relative min-h-[340px] overflow-visible sm:min-h-[420px] lg:min-h-[480px]">
          <div
            ref={mountRef}
            data-three-ebook-mount
            className="absolute inset-0 cursor-grab touch-pan-y active:cursor-grabbing"
            aria-label="Interactive 3D preview of an ebook plan assembling: topic, audience, chapters, and a final prompt. Drag to rotate."
            role="img"
          />
          <StaticBookPreview isHidden={canvasReady} />
          {sceneState === "fallback" && (
            <p className="absolute bottom-0 left-0 right-0 text-center text-xs text-gray-400">
              Animated preview paused on this device.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default EbookOrbitShowcase;
